import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { OpportunityService } from '@/services/opportunity.service';
import { OpportunityStore } from '@/store/opportunity/opportunity.store';
import * as TaskOpportunityMetricsActions from './task-opportunity-metrics.actions';
import { catchError, map, mergeMap, of, tap } from 'rxjs';

@Injectable()
export class TaskOpportunityMetricsEffects {
  private actions$ = inject(Actions);
  private opportunityStore = inject(OpportunityStore);
  constructor(private opportunityService: OpportunityService) {}

  loadMetrics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskOpportunityMetricsActions.loadTaskOpportunityMetrics),
      mergeMap(({ opportunityId }) =>
        this.opportunityService.getOpportunityMetrics(opportunityId).pipe(
          map((metrics) => TaskOpportunityMetricsActions.loadTaskOpportunityMetricsSuccess({ metrics })),
          catchError((error) => of(TaskOpportunityMetricsActions.loadTaskOpportunityMetricsFailure({ error })))
        )
      )
    )
  );

  createMetric$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskOpportunityMetricsActions.createTaskOpportunityMetric),
      mergeMap(({ opportunityId, metric }) =>
        this.opportunityService.createOpportunityMetric(opportunityId, metric).pipe(
          map((createdMetric) => TaskOpportunityMetricsActions.createTaskOpportunityMetricSuccess({ metric: createdMetric, opportunityId })),
          catchError((error) => of(TaskOpportunityMetricsActions.createTaskOpportunityMetricFailure({ error })))
        )
      )
    )
  );

  updateMetric$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskOpportunityMetricsActions.updateTaskOpportunityMetric),
      mergeMap(({ metricId, changes, opportunityId }) =>
        this.opportunityService.updateOpportunityMetric(metricId, changes).pipe(
          map((updatedMetric) => TaskOpportunityMetricsActions.updateTaskOpportunityMetricSuccess({ metric: updatedMetric, opportunityId })),
          catchError((error) => of(TaskOpportunityMetricsActions.updateTaskOpportunityMetricFailure({ error })))
        )
      )
    )
  );

  deleteMetric$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskOpportunityMetricsActions.deleteTaskOpportunityMetric),
      mergeMap(({ metricId, opportunityId }) =>
        this.opportunityService.deleteOpportunityMetric(metricId).pipe(
          map(() => TaskOpportunityMetricsActions.deleteTaskOpportunityMetricSuccess({ metricId, opportunityId })),
          catchError((error) => of(TaskOpportunityMetricsActions.deleteTaskOpportunityMetricFailure({ error })))
        )
      )
    )
  );

  // Workflow status refresh effects - update opportunity state after metric changes
  // that may affect workflow stage transitions (e.g., DRAFTING_CHARTER → CHARTER_COMPLETE)

  refreshWorkflowStatusAfterCreate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TaskOpportunityMetricsActions.createTaskOpportunityMetricSuccess),
        tap(({ opportunityId }) => {
          if (opportunityId) {
            this.opportunityStore.refreshWorkflowStatus(opportunityId);
          }
        })
      ),
    { dispatch: false }
  );

  refreshWorkflowStatusAfterUpdate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TaskOpportunityMetricsActions.updateTaskOpportunityMetricSuccess),
        tap(({ opportunityId }) => {
          if (opportunityId) {
            this.opportunityStore.refreshWorkflowStatus(opportunityId);
          }
        })
      ),
    { dispatch: false }
  );

  refreshWorkflowStatusAfterDelete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TaskOpportunityMetricsActions.deleteTaskOpportunityMetricSuccess),
        tap(({ opportunityId }) => {
          if (opportunityId) {
            this.opportunityStore.refreshWorkflowStatus(opportunityId);
          }
        })
      ),
    { dispatch: false }
  );
}
